import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { fetchChatRooms, fetchMessages, sendMessage } from '../../store/slices/chatSlice';
import socketService from '../../services/socket';
import {
  Box, Typography, List, ListItem, ListItemText, TextField, IconButton,
  Paper, Divider, Avatar, Badge, CircularProgress, Chip
} from '@mui/material';
import { Send, SmartToy } from '@mui/icons-material';
import ChatMessageBubble from '../../components/ChatMessageBubble';

const SupplierChat = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { rooms: chatRooms = [], messages = {}, loading } = useSelector(state => state.chat);
  const { user } = useSelector(state => state.auth);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messageText, setMessageText] = useState('');
  const messagesEndRef = useRef(null);
  const activeMessages = selectedRoom ? (messages[selectedRoom._id] || []) : [];
  const getSenderId = (msg) => msg?.sender?._id || msg?.sender;
  const isOwnMessage = (msg) => getSenderId(msg) === user?._id;
  const getRoomPrimaryLabel = (room) => room?.customer?.label || `Customer No: ${room?.customer?.customerId || 'N/A'}`;
  const getRoomSecondaryLabel = (room) => {
    const details = [room?.roomId, room?.product?.name || room?.type || 'General Inquiry'].filter(Boolean);
    return details.join(' | ');
  };
  const getMessageSenderLabel = (msg) => {
    if (isOwnMessage(msg)) return '';
    if (msg?.senderRole === 'system' || msg?.senderRole === 'ai_bot') return 'OSESG';
    return 'OSESG';
  };

  useEffect(() => { dispatch(fetchChatRooms()); }, [dispatch]);

  useEffect(() => {
    if (selectedRoom) {
      dispatch(fetchMessages({ roomId: selectedRoom._id }));
      socketService.joinRoom(selectedRoom._id);
      return () => socketService.leaveRoom(selectedRoom._id);
    }
  }, [dispatch, selectedRoom]);

  useEffect(() => {
    const handleRoomRefresh = () => {
      dispatch(fetchChatRooms());
      if (selectedRoom?._id) {
        dispatch(fetchMessages({ roomId: selectedRoom._id }));
      }
    };

    socketService.onNewMessage((payload) => {
      dispatch(fetchChatRooms());
      if (selectedRoom?._id === payload.roomId) {
        dispatch(fetchMessages({ roomId: payload.roomId }));
      }
    });
    socketService.onChatRoomUpdated(handleRoomRefresh);

    return () => {
      socketService.off('new_message');
      socketService.off('chat_room_updated');
    };
  }, [dispatch, selectedRoom]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSend = (e) => {
    e.preventDefault();
    if (!messageText.trim() || !selectedRoom) return;
    dispatch(sendMessage({ roomId: selectedRoom._id, content: messageText }));
    setMessageText('');
  };

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 120px)' }}>
      {/* Rooms List */}
      <Paper sx={{ width: 300, overflow: 'auto', borderRight: '1px solid #eee' }}>
        <Box sx={{ p: 2 }}><Typography variant="h6" fontWeight={600}>{t('messages')}</Typography></Box>
        <Divider />
        <List>
          {chatRooms.map((room) => (
            <ListItem key={room._id} button selected={selectedRoom?._id === room._id}
              onClick={() => setSelectedRoom(room)}
              sx={{ '&.Mui-selected': { bgcolor: '#e8eaf6' } }}>
              <ListItemText
                primary={getRoomPrimaryLabel(room)}
                secondary={getRoomSecondaryLabel(room)}
                primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
                secondaryTypographyProps={{ fontSize: '0.75rem', noWrap: true }}
              />
              {room.unreadCount > 0 && (
                <Badge badgeContent={room.unreadCount} color="primary" />
              )}
            </ListItem>
          ))}
          {chatRooms.length === 0 && (
            <Box p={2}><Typography variant="body2" color="text.secondary">No conversations yet</Typography></Box>
          )}
        </List>
      </Paper>

      {/* Chat Area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {selectedRoom ? (
          <>
            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderBottom: '1px solid #eee' }}>
              <Typography variant="subtitle1" fontWeight={600}>{getRoomPrimaryLabel(selectedRoom)}</Typography>
              <Typography variant="caption" color="text.secondary">
                {getRoomSecondaryLabel(selectedRoom)}
              </Typography>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              {loading ? <CircularProgress /> : activeMessages.map((msg, i) => <ChatMessageBubble key={i} msg={msg} isOwn={isOwnMessage(msg)} label={getMessageSenderLabel(msg)} />)}
              {!loading && activeMessages.length === 0 && (
                <Typography variant="body2" color="text.secondary">No messages yet</Typography>
              )}
              <div ref={messagesEndRef} />
            </Box>

            <Box component="form" onSubmit={handleSend} sx={{ p: 2, display: 'flex', gap: 1, borderTop: '1px solid #eee' }}>
              <TextField fullWidth size="small" placeholder="Type a message..."
                value={messageText} onChange={(e) => setMessageText(e.target.value)} />
              <IconButton type="submit" color="primary"><Send /></IconButton>
            </Box>
          </>
        ) : (
          <Box display="flex" alignItems="center" justifyContent="center" flex={1}>
            <Typography variant="body1" color="text.secondary">Select a conversation to start chatting</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SupplierChat;
